import { Plus } from "lucide-react";
import { Kbd } from "./kbd";
import { cn } from "@/lib/utils";

interface FilterToolbarProps {
  entity: string;
  /** Optional add-filter affordance. Visual-only by default. */
  onAddFilter?: () => void;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Filter toolbar: `Filter <entity>` pseudo-input + `/` kbd hint + chip slot +
 * `+ Filter` add affordance + flex spacer. Mirrors `.toolbar` from
 * mockup--opportunity-list.css:120-167.
 *
 * Pressing `/` opens the global command palette via the existing
 * SHORTCUTS.openSearch handler — per-page focus capture is intentionally not
 * implemented here.
 */
export function FilterToolbar({
  entity,
  onAddFilter,
  children,
  className,
}: FilterToolbarProps) {
  const filterLabel = `Filter ${entity}`;
  return (
    <div
      className={cn("flex flex-wrap items-center gap-2 py-2 pb-3", className)}
    >
      <div
        role="presentation"
        aria-label={filterLabel}
        className={cn(
          "min-w-[240px] inline-flex items-center gap-2 px-3 py-1.5",
          "rounded-[var(--radius-sm)] border border-[color:var(--color-border)]",
          "bg-[color:var(--color-surface)] text-[color:var(--color-text-md)]",
          "typo-body select-none",
        )}
      >
        <span>{filterLabel}</span>
        <Kbd className="ml-auto">/</Kbd>
      </div>
      {children}
      {onAddFilter && (
        <button
          type="button"
          onClick={onAddFilter}
          className={cn(
            "inline-flex items-center gap-1 px-3 py-1",
            "rounded-[var(--radius-sm)] border border-[color:var(--color-border)]",
            "bg-[color:var(--color-surface)] text-[color:var(--color-text-md)]",
            "typo-label hover:text-[color:var(--color-text-hi)]",
            "transition-colors cursor-pointer",
          )}
        >
          <Plus className="h-3 w-3" aria-hidden="true" />
          Filter
        </button>
      )}
      <div className="flex-1" />
    </div>
  );
}
